import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { t } from '../../lib/i18n';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import { Bug } from 'lucide-react';

export const BugReportButton = () => {
  const { language } = useAuth();
  const [canSubmit, setCanSubmit] = useState(false);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    type: 'bug',
    title: '',
    description: '',
    priority: 'medium',
  });

  const checkPermission = useCallback(async () => {
    try {
      const res = await axios.get('/api/reports/can-submit');
      setCanSubmit(res.data.allowed);
    } catch {
      setCanSubmit(false);
    }
  }, []);

  useEffect(() => {
    checkPermission();
  }, [checkPermission]);

  const resetForm = () => {
    setForm({ type: 'bug', title: '', description: '', priority: 'medium' });
  };

  const handleSubmit = async () => {
    if (!form.title.trim()) {
      toast.error(t('report_title', language) + ' required');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post('/api/reports', form);
      toast.success(t('report_success', language));
      resetForm();
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setSubmitting(false);
    }
  };

  if (!canSubmit) return null;

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        className="h-9 w-9"
        onClick={() => setOpen(true)}
        data-testid="bug-report-button"
      >
        <Bug className="h-4 w-4" />
      </Button>

      <Dialog open={open} onOpenChange={(v) => { if (!v) resetForm(); setOpen(v); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('report_dialog_title', language)}</DialogTitle>
            <DialogDescription>
              {language === 'fr'
                ? 'Décrivez le problème rencontré ou l\'amélioration souhaitée'
                : 'Describe the issue or desired improvement'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t('report_type', language)}</Label>
              <Select value={form.type} onValueChange={(v) => setForm(prev => ({ ...prev, type: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bug">{t('report_type_bug', language)}</SelectItem>
                  <SelectItem value="feature">{t('report_type_feature', language)}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>{t('report_title', language)}</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder={t('report_title', language)}
              />
            </div>

            <div className="space-y-2">
              <Label>{t('report_description', language)}</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder={t('report_description', language)}
                rows={4}
              />
            </div>

            <div className="space-y-2">
              <Label>{t('report_priority', language)}</Label>
              <Select value={form.priority} onValueChange={(v) => setForm(prev => ({ ...prev, priority: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">{t('report_priority_low', language)}</SelectItem>
                  <SelectItem value="medium">{t('report_priority_medium', language)}</SelectItem>
                  <SelectItem value="high">{t('report_priority_high', language)}</SelectItem>
                  <SelectItem value="critical">{t('report_priority_critical', language)}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { resetForm(); setOpen(false); }}>
              {t('cancel', language)}
            </Button>
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting ? t('report_submitting', language) : t('report_submit', language)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BugReportButton;
